import { EntriesClient } from "~/api/entries";
import type { ILink, IParagraphEntry, IUserEntry } from "~/models/IUserEntry";
import type { QueryKey, Updater } from "@tanstack/vue-query";
import type { ChronikEntry } from "~/models/IBookshelf";

type ContextItem<T> = {
  data: T | undefined;
  key: QueryKey;
};

export const entriesMutations = () => {
  const queryClient = useQueryClient();
  const apiClient = useApiClient(EntriesClient);
  const resolvedQueries = queries();

  const storeSingleEntry = (data: IParagraphEntry) => {
    queryClient.setQueryData(
      resolvedQueries.entries.byType(data.type)._ctx.byId(data.id).queryKey,
      data
    );

    if ("slug" in data && data.slug) {
      queryClient.setQueryData(
        resolvedQueries.entries.byType(data.type)._ctx.bySlug(data.slug)
          .queryKey,
        data
      );
    }
  };

  type InvalidatedDomains =
    | "bookshelf"
    | "entries"
    | "paragraphs"
    | "fieldOfLaws";

  const invalidateAllEntries = <T extends ChronikEntry | IUserEntry>(
    entry: T
  ): Record<InvalidatedDomains, Promise<Array<void>>> => {
    const queriesToInvalidate: Record<InvalidatedDomains, QueryKey[]> = {
      bookshelf: [resolvedQueries.bookshelf._def],
      entries: [],
      paragraphs: [],
      fieldOfLaws: [],
    };

    if ("slug" in entry && entry.slug) {
      queriesToInvalidate.entries.push(
        resolvedQueries.entries.byType(entry.type)._ctx.bySlug(entry.slug)
          .queryKey
      );
    }

    const invalidateLink = (entry: ILink) => {
      queriesToInvalidate.entries.push(
        resolvedQueries.entries
          .byType("link_chain")
          ._ctx.byId(entry.link_chain_id).queryKey
      );
      queriesToInvalidate.paragraphs.push(
        resolvedQueries.paragraphs
          .byId(entry.legal_text_paragraph_id)
          ._ctx.entriesByParagraph("link_chain").queryKey,
        resolvedQueries.paragraphs.byId(entry.legal_text_paragraph_linked_id)
          ._ctx.incomingLinkChains.queryKey
      );
    };

    // invalidate parent link chain
    if (entry.type === "link") {
      invalidateLink(entry);
    } else if (entry.type === "link_chain") {
      entry.links.forEach(invalidateLink);
    } else if (entry.type !== "bookmark") {
      queriesToInvalidate.entries.push(
        resolvedQueries.entries.byType(entry.type)._ctx.byId(entry.id).queryKey
      );

      queriesToInvalidate.paragraphs.push(
        resolvedQueries.paragraphs
          .byId(entry.legal_text_paragraph_id)
          ._ctx.entriesByParagraph(entry.type).queryKey
      );
    }

    // invalidate field of law schemas
    if (
      entry.type === "schema" &&
      entry.legal_text_paragraph.field_of_law_primary_id
    ) {
      queriesToInvalidate.fieldOfLaws.push(
        resolvedQueries.fieldOfLaws.byId(
          entry.legal_text_paragraph.field_of_law_primary_id
        )._ctx.schemas.queryKey
      );
    }

    return Object.fromEntries(
      Object.entries(queriesToInvalidate).map(([key, queryKeys]) => [
        key,
        invalidateKeys(queryClient, queryKeys),
      ])
    ) as unknown as Record<InvalidatedDomains, Promise<Array<void>>>;
  };

  const optimisticUpdate = async <T, U = Updater<T | undefined, T | undefined>>(
    queryKey: QueryKey,
    updater: U
  ) => {
    await queryClient.cancelQueries({ queryKey });
    const previousData = queryClient.getQueryData<T>(queryKey);
    queryClient.setQueryData(queryKey, updater);
    return previousData;
  };

  const createEntryMutation = useMutation({
    mutationFn: apiClient.saveEntry,
    onSuccess: async (data) => {
      storeSingleEntry(data);

      const { entries, paragraphs, fieldOfLaws } = invalidateAllEntries(data);
      return Promise.all([entries, paragraphs, fieldOfLaws]);
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: apiClient.updateEntry,
    onMutate: async (data) => {
      const singleUpdater = (old?: IParagraphEntry) => ({
        ...(old ?? {}),
        ...data,
      });

      const previous: ContextItem<IParagraphEntry | IParagraphEntry[]>[] = [];

      const key = resolvedQueries.entries
        .byType(data.type)
        ._ctx.byId(data.id).queryKey;

      previous.push({
        data: await optimisticUpdate<IParagraphEntry>(key, singleUpdater),
        key,
      });

      const listUpdater = replaceUpdater(data, (entry) => entry.id === data.id);

      const paragraphKey = resolvedQueries.paragraphs
        .byId(data.legal_text_paragraph_id)
        ._ctx.entriesByParagraph(data.type).queryKey;

      previous.push({
        key: paragraphKey,
        data: await optimisticUpdate<IParagraphEntry[]>(
          paragraphKey,
          listUpdater
        ),
      });

      if (
        data.type === "schema" &&
        data.legal_text_paragraph.field_of_law_primary_id
      ) {
        const schemaQueryKey = resolvedQueries.fieldOfLaws.byId(
          data.legal_text_paragraph.field_of_law_primary_id
        )._ctx.schemas.queryKey;

        previous.push({
          key: schemaQueryKey,
          data: await optimisticUpdate<IParagraphEntry[]>(
            schemaQueryKey,
            listUpdater
          ),
        });

        if (data.slug) {
          const slugKey = resolvedQueries.entries
            .byType(data.type)
            ._ctx.bySlug(data.slug).queryKey;

          previous.push({
            key: slugKey,
            data: await optimisticUpdate<IParagraphEntry>(
              slugKey,
              singleUpdater
            ),
          });
        }
      }

      return { previous };
    },
    onError: (_err, _data, context) => {
      if (!context) return;

      // rollback
      context.previous.forEach(({ key, data }) => {
        if (data) {
          queryClient.setQueryData(key, data);
        }
      });
    },
    onSettled: async (newEntry, __, data, context) => {
      const keysToInvalidate: QueryKey[] = [];
      if (newEntry?.legal_text_paragraph_id !== data.legal_text_paragraph_id) {
        keysToInvalidate.push(
          resolvedQueries.paragraphs
            .byId(newEntry?.legal_text_paragraph_id)
            ._ctx.entriesByParagraph(data.type).queryKey
        );
      }

      if (newEntry?.type === "link_chain") {
        keysToInvalidate.push(
          ...newEntry.links.map(
            ({ legal_text_paragraph_linked_id }) =>
              resolvedQueries.paragraphs.byId(legal_text_paragraph_linked_id)
                ._ctx.incomingLinkChains.queryKey
          )
        );
      }

      void invalidateKeys(queryClient, keysToInvalidate);

      if (!context) return;

      void invalidateKeys(queryClient, [
        ...context.previous.map(({ key }) => key),
        resolvedQueries.bookshelf._def,
      ]);
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: apiClient.deleteEntry,
    onSuccess: (_, entry) => {
      if (entry.type !== "link") {
        queryClient.setQueryData(
          resolvedQueries.paragraphs
            .byId(entry.legal_text_paragraph_id)
            ._ctx.entriesByParagraph(entry.type).queryKey,
          deleteUpdater<IParagraphEntry>((val) => val.id === entry.id)
        );

        if (
          entry.type === "schema" &&
          entry.legal_text_paragraph.field_of_law_primary_id
        ) {
          queryClient.setQueryData(
            resolvedQueries.fieldOfLaws.byId(
              entry.legal_text_paragraph.field_of_law_primary_id
            )._ctx.schemas.queryKey,
            deleteUpdater<IParagraphEntry>((val) => val.id === entry.id)
          );
        }
      }

      invalidateAllEntries(entry);
    },
  });

  const restoreEntryMutation = useMutation({
    mutationFn: apiClient.restoreEntry,
    onSuccess: (entry) => {
      const { entries, paragraphs } = invalidateAllEntries(entry);
      return Promise.all([entries, paragraphs]);
    },
  });

  return {
    createEntryMutation,
    updateEntryMutation,
    deleteEntryMutation,
    restoreEntryMutation,
    invalidateAllEntries,
  };
};
